import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, isCollapsed, onToggle }) => {
  const registryOptions = [
    { value: 'all', label: 'All Registries' },
    { value: 'verra', label: 'Verra (VCS)' },
    { value: 'gold-standard', label: 'Gold Standard' },
    { value: 'acr', label: 'American Carbon Registry' },
    { value: 'art', label: 'Architecture for REDD+ Transactions' },
    { value: 'gcc', label: 'Global Carbon Council' },
    { value: 'cad-trust', label: 'CAD Trust' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'forestry', label: 'Forestry & Land Use' },
    { value: 'renewable-energy', label: 'Renewable Energy' },
    { value: 'energy-efficiency', label: 'Energy Efficiency' },
    { value: 'waste-management', label: 'Waste Management' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'industrial', label: 'Industrial Processes' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'blue-carbon', label: 'Blue Carbon' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'approved', label: 'Approved' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'revised', label: 'Recently Revised' },
    { value: 'deprecated', label: 'Deprecated' }
  ];

  const riskOptions = [
    { value: 'all', label: 'All Risk Levels' },
    { value: 'low', label: 'Low Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'high', label: 'High Risk' }
  ];

  const vintageOptions = [
    { value: 'all', label: 'All Vintages' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' },
    { value: '2022', label: '2022' },
    { value: '2021', label: '2021' },
    { value: '2020', label: '2020' },
    { value: 'older', label: '2019 & Earlier' }
  ];

  return (
    <div className={`bg-card border border-border rounded-lg transition-all duration-300 ${
      isCollapsed ? 'w-12' : 'w-80'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        {!isCollapsed && (
          <h3 className="font-semibold text-card-foreground">Filters</h3>
        )}
        <button
          onClick={onToggle}
          className="p-1 hover:bg-muted rounded transition-smooth"
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
        </button>
      </div>
      {!isCollapsed && (
        <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Search */}
          <div>
            <Input
              type="search"
              placeholder="Search methodologies..."
              value={filters?.search}
              onChange={(e) => onFilterChange('search', e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Registry Filter */}
          <div>
            <Select
              label="Registry"
              options={registryOptions}
              value={filters?.registry}
              onChange={(value) => onFilterChange('registry', value)}
            />
          </div>

          {/* Type Filter */}
          <div>
            <Select
              label="Methodology Type"
              options={typeOptions}
              value={filters?.type}
              onChange={(value) => onFilterChange('type', value)}
            />
          </div>

          {/* Status Filter */}
          <div>
            <Select
              label="Status"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => onFilterChange('status', value)}
            />
          </div>

          {/* Risk Level Filter */}
          <div>
            <Select
              label="Risk Level"
              options={riskOptions}
              value={filters?.riskLevel}
              onChange={(value) => onFilterChange('riskLevel', value)}
            />
          </div>

          {/* Vintage Filter */}
          <div>
            <Select
              label="Vintage Year"
              options={vintageOptions}
              value={filters?.vintage}
              onChange={(value) => onFilterChange('vintage', value)}
            />
          </div>

          {/* Rating Range */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Minimum Rating
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={filters?.minRating}
                onChange={(e) => onFilterChange('minRating', parseFloat(e?.target?.value))}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-8">
                {filters?.minRating}
              </span>
            </div>
          </div>

          {/* Special Flags */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Special Conditions
            </label>
            <div className="space-y-2">
              <Checkbox
                label="Has Scrutiny Flags"
                checked={filters?.hasScrutinyFlags}
                onChange={(e) => onFilterChange('hasScrutinyFlags', e?.target?.checked)}
              />
              <Checkbox
                label="Recently Revised"
                checked={filters?.recentlyRevised}
                onChange={(e) => onFilterChange('recentlyRevised', e?.target?.checked)}
              />
              <Checkbox
                label="Peer Reviewed"
                checked={filters?.peerReviewed}
                onChange={(e) => onFilterChange('peerReviewed', e?.target?.checked)}
              />
              <Checkbox
                label="High Market Adoption"
                checked={filters?.highAdoption}
                onChange={(e) => onFilterChange('highAdoption', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Average Price Range ($)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={filters?.minPrice}
                onChange={(e) => onFilterChange('minPrice', e?.target?.value)}
              />
              <Input
                type="number"
                placeholder="Max"
                value={filters?.maxPrice}
                onChange={(e) => onFilterChange('maxPrice', e?.target?.value)}
              />
            </div>
          </div>

          {/* Clear Filters */}
          <div className="pt-4 border-t border-border">
            <Button
              variant="outline"
              fullWidth
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
            >
              Clear All Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;